// Room tilemap: builds the collision layer (autotiled solids, one-way platforms,
// hazards) and the decorative back layers from a parsed template, and answers
// tile queries (solid / one-way / raycast / ground below) for the rest of the game.

import Phaser from 'phaser';
import { TILE_FRAMES } from '../../art/keys';
import { DEPTH, TILE } from '../../config/constants';
import { Rng, hashSeed } from '../../core/rng';
import type { SectorDef } from '../../core/sector';
import { CELL, type CellKind, type RoomLayout } from './template';
import { tilesetTex } from './textures';

/** Horizontal run of cells on one row (tile units, inclusive). */
export interface CellRun {
  x0: number;
  x1: number;
  y: number;
}

/** Opening in the left/right room edge, blocked by a door. */
export interface EdgeDoor {
  side: -1 | 1;
  /** Tile column of the edge. */
  tx: number;
  /** First and last open row. */
  ty0: number;
  ty1: number;
}

const PLATFORM_FRAMES = new Set<number>([
  TILE_FRAMES.platformLeft,
  TILE_FRAMES.platformMid,
  TILE_FRAMES.platformRight,
  TILE_FRAMES.platformSingle,
]);

/** Landing tolerance for one-way platforms: feet may be this far below the top in the previous step. */
const ONE_WAY_TOLERANCE = 6;

export class RoomMap {
  readonly layout: RoomLayout;
  readonly cols: number;
  readonly rows: number;
  readonly width: number;
  readonly height: number;
  readonly map: Phaser.Tilemaps.Tilemap;
  readonly solids: Phaser.Tilemaps.TilemapLayer;
  readonly back: Phaser.Tilemaps.TilemapLayer;
  readonly decor: Phaser.Tilemaps.TilemapLayer;
  /** Physics kind per cell (template cells plus sealed edge doors). */
  readonly cells: Uint8Array;
  readonly doors: EdgeDoor[] = [];
  readonly hazardRuns: CellRun[] = [];
  readonly platformRuns: CellRun[] = [];
  /** Floor tops open to the sky (nothing solid above): rain splash surfaces. */
  readonly skySurfaces: CellRun[] = [];
  /** 0..1 share of the top row that is open sky (rain intensity). */
  readonly skyOpenness: number;

  private colliders: Phaser.Physics.Arcade.Collider[] = [];
  private destroyed = false;

  constructor(
    private scene: Phaser.Scene,
    layout: RoomLayout,
    sector: SectorDef,
  ) {
    this.layout = layout;
    this.cols = layout.w;
    this.rows = layout.h;
    this.width = layout.w * TILE;
    this.height = layout.h * TILE;
    this.cells = new Uint8Array(layout.cells);
    this.findDoors();
    this.findRuns();

    let open = 0;
    for (let x = 0; x < this.cols; x++) if (this.cells[x] === CELL.EMPTY) open++;
    this.skyOpenness = open / this.cols;

    const key = tilesetTex(scene, sector);
    this.map = scene.make.tilemap({ tileWidth: TILE, tileHeight: TILE, width: this.cols, height: this.rows });
    const tileset = this.map.addTilesetImage(key, key, TILE, TILE, 0, 0)!;
    this.back = this.map.createBlankLayer('back', tileset, 0, 0)!.setDepth(DEPTH.WORLD_BACK);
    this.decor = this.map.createBlankLayer('decor', tileset, 0, 0)!.setDepth(DEPTH.WORLD_BACK + 1);
    this.solids = this.map.createBlankLayer('solids', tileset, 0, 0)!.setDepth(DEPTH.TILES);
    this.paintSolids(new Rng(hashSeed('world-tiles', layout.id, sector.biome)));
    this.paintBack(new Rng(hashSeed('world-decor', layout.id, sector.biome)));
  }

  // ───────────────────────────── Build ─────────────────────────────

  /** Left/right edge openings become sealed door cells (solid for physics, drawn as doors). */
  private findDoors(): void {
    for (const tx of [0, this.cols - 1]) {
      let start = -1;
      for (let ty = 0; ty <= this.rows; ty++) {
        const open = ty < this.rows && this.cells[ty * this.cols + tx] !== CELL.SOLID;
        if (open && start < 0) start = ty;
        if (!open && start >= 0) {
          this.doors.push({ side: tx === 0 ? -1 : 1, tx, ty0: start, ty1: ty - 1 });
          for (let y = start; y < ty; y++) this.cells[y * this.cols + tx] = CELL.SOLID;
          start = -1;
        }
      }
    }
  }

  private findRuns(): void {
    for (let y = 0; y < this.rows; y++) {
      let hz = -1;
      let pf = -1;
      for (let x = 0; x <= this.cols; x++) {
        const k = x < this.cols ? this.cells[y * this.cols + x] : -1;
        if (k === CELL.HAZARD && hz < 0) hz = x;
        if (k !== CELL.HAZARD && hz >= 0) {
          this.hazardRuns.push({ x0: hz, x1: x - 1, y });
          hz = -1;
        }
        if (k === CELL.ONEWAY && pf < 0) pf = x;
        if (k !== CELL.ONEWAY && pf >= 0) {
          this.platformRuns.push({ x0: pf, x1: x - 1, y });
          pf = -1;
        }
      }
    }
    // Sky-exposed surfaces: first ground cell from the top of each column, if nothing above it.
    let run: CellRun | null = null;
    for (let x = 0; x < this.cols; x++) {
      let y = 0;
      while (y < this.rows && this.cells[y * this.cols + x] === CELL.EMPTY) y++;
      const ok = y > 0 && y < this.rows;
      if (ok && run && run.y === y && run.x1 === x - 1) run.x1 = x;
      else if (ok) this.skySurfaces.push((run = { x0: x, x1: x, y }));
      else run = null;
    }
  }

  private isBlockingCell(x: number, y: number): boolean {
    if (x < 0 || y < 0 || x >= this.cols || y >= this.rows) return true;
    const k = this.cells[y * this.cols + x];
    return k === CELL.SOLID || k === CELL.HAZARD;
  }

  private isDoorCell(x: number, y: number): boolean {
    return this.doors.some((d) => d.tx === x && y >= d.ty0 && y <= d.ty1);
  }

  /** Autotile frame for a cell from its current neighbours (rng adds interior detail panels). */
  private frameFor(x: number, y: number, rng: Rng | null): number {
    const { cols } = this;
    const k = this.cells[y * cols + x] as CellKind;
    if (k === CELL.HAZARD) return TILE_FRAMES.hazard;
    if (k === CELL.ONEWAY) {
      const l = x > 0 && this.cells[y * cols + x - 1] === CELL.ONEWAY;
      const r = x < cols - 1 && this.cells[y * cols + x + 1] === CELL.ONEWAY;
      return l && r ? TILE_FRAMES.platformMid : l ? TILE_FRAMES.platformRight : r ? TILE_FRAMES.platformLeft : TILE_FRAMES.platformSingle;
    }
    const mask =
      (this.isBlockingCell(x, y - 1) ? 1 : 0) |
      (this.isBlockingCell(x + 1, y) ? 2 : 0) |
      (this.isBlockingCell(x, y + 1) ? 4 : 0) |
      (this.isBlockingCell(x - 1, y) ? 8 : 0);
    let frame = TILE_FRAMES.solidMaskBase + mask;
    // Large interior fills get occasional detail panels (never next to each other).
    if (mask === 15 && rng) {
      const r = rng.next();
      const left = x > 0 ? this.solids.getTileAt(x - 1, y) : null;
      const busy = left && left.index !== TILE_FRAMES.solidMaskBase + 15;
      if (!busy && r < 0.07) frame = TILE_FRAMES.solidDetailA;
      else if (!busy && r < 0.12) frame = TILE_FRAMES.solidDetailB;
    }
    return frame;
  }

  private placeTile(x: number, y: number, frame: number): void {
    const k = this.cells[y * this.cols + x];
    // No Phaser face recalculation: faces are set by applyFaces (one-way rules).
    const tile = this.solids.putTileAt(frame, x, y, false);
    if (k === CELL.ONEWAY) tile.setCollision(false, false, true, false, false);
    else tile.setCollision(true, true, true, true, false);
    if (this.isDoorCell(x, y)) tile.setVisible(false);
  }

  /**
   * Collision faces: computed here instead of Phaser's neighbour rule so one-way
   * tiles never hide a wall face and only ever expose their top.
   */
  private applyFaces(x: number, y: number): void {
    const tile = this.solids.getTileAt(x, y);
    if (!tile) return;
    const k = this.cells[y * this.cols + x];
    tile.faceTop = !this.isBlockingCell(x, y - 1);
    if (k === CELL.ONEWAY) {
      tile.faceBottom = tile.faceLeft = tile.faceRight = false;
    } else {
      tile.faceBottom = !this.isBlockingCell(x, y + 1);
      tile.faceLeft = !this.isBlockingCell(x - 1, y);
      tile.faceRight = !this.isBlockingCell(x + 1, y);
    }
  }

  private paintSolids(rng: Rng): void {
    const { cols, rows } = this;
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        if (this.cells[y * cols + x] === CELL.EMPTY) continue;
        this.placeTile(x, y, this.frameFor(x, y, rng));
      }
    }
    for (let y = 0; y < rows; y++) for (let x = 0; x < cols; x++) this.applyFaces(x, y);
  }

  // ───────────────────────────── Destruction ─────────────────────────────

  /** Cells that must never break: floors under props / spawns (1 = protected). */
  private protectedCells: Uint8Array | null = null;

  private buildProtection(): Uint8Array {
    const { cols, rows } = this;
    const prot = new Uint8Array(cols * rows);
    // Floor under gameplay anchors, by half-width in tiles.
    const halfWidth: Partial<Record<keyof RoomLayout['markers'], number>> = { P: 1, X: 2, S: 2.2, R: 1.6, T: 0.8, B: 3 };
    for (const [key, hw] of Object.entries(halfWidth) as [keyof RoomLayout['markers'], number][]) {
      for (const m of this.layout.markers[key] ?? []) {
        const ty = m.ty + 1;
        if (ty < 0 || ty >= rows) continue;
        for (let tx = Math.floor(m.cx - hw); tx <= Math.floor(m.cx + hw - 1e-3); tx++) {
          if (tx >= 0 && tx < cols) prot[ty * cols + tx] = 1;
        }
      }
    }
    return prot;
  }

  /** Interior solid ground that may be damaged and broken (the outer frame, doors and prop floors never break). */
  isBreakable(tx: number, ty: number): boolean {
    if (tx <= 0 || ty <= 0 || tx >= this.cols - 1 || ty >= this.rows - 1) return false;
    const i = ty * this.cols + tx;
    if (this.cells[i] !== CELL.SOLID || this.isDoorCell(tx, ty)) return false;
    this.protectedCells ??= this.buildProtection();
    return this.protectedCells[i] === 0;
  }

  /** Remove a breakable cell and re-tile its neighbours. Returns false if it cannot break. */
  breakCell(tx: number, ty: number): boolean {
    if (this.destroyed || !this.isBreakable(tx, ty)) return false;
    const { cols, rows } = this;
    this.cells[ty * cols + tx] = CELL.EMPTY;
    this.solids.removeTileAt(tx, ty, true, false);
    // The hole shows a dark cavity instead of the sky.
    if (!this.back.getTileAt(tx, ty)) this.back.putTileAt(TILE_FRAMES.backWall, tx, ty);
    this.decor.removeTileAt(tx, ty);
    for (let y = ty - 1; y <= ty + 1; y++) {
      for (let x = tx - 1; x <= tx + 1; x++) {
        if (x < 0 || y < 0 || x >= cols || y >= rows || this.cells[y * cols + x] === CELL.EMPTY) continue;
        const cur = this.solids.getTileAt(x, y);
        const next = this.frameFor(x, y, null);
        // Keep interior detail panels while the cell is still fully enclosed.
        const keep = cur && next === TILE_FRAMES.solidMaskBase + 15 && (cur.index === TILE_FRAMES.solidDetailA || cur.index === TILE_FRAMES.solidDetailB);
        if (!keep && (!cur || cur.index !== next)) this.placeTile(x, y, next);
      }
    }
    for (let y = ty - 1; y <= ty + 1; y++) for (let x = tx - 1; x <= tx + 1; x++) if (x >= 0 && y >= 0 && x < cols && y < rows) this.applyFaces(x, y);
    return true;
  }

  /** Back walls plus seeded decor (vents, pipes, grates, warning stripes) for depth. */
  private paintBack(rng: Rng): void {
    const { cols, rows } = this;
    const back = this.layout.back;
    const isBack = (x: number, y: number) =>
      x >= 0 && y >= 0 && x < cols && y < rows && back[y * cols + x] === 1 && !this.isBlockingCell(x, y);
    for (let y = 0; y < rows; y++) for (let x = 0; x < cols; x++) if (isBack(x, y)) this.back.putTileAt(TILE_FRAMES.backWall, x, y);

    const used = new Uint8Array(cols * rows);
    const free = (x: number, y: number) => isBack(x, y) && !used[y * cols + x] && this.cells[y * cols + x] === CELL.EMPTY;
    const put = (frame: number, x: number, y: number) => {
      this.decor.putTileAt(frame, x, y);
      used[y * cols + x] = 1;
    };

    // Pipes hugging ceilings: horizontal runs directly under solid cells.
    for (let y = 1; y < rows; y++) {
      let x = 0;
      while (x < cols) {
        if (!(free(x, y) && this.isBlockingCell(x, y - 1))) {
          x++;
          continue;
        }
        let end = x;
        while (end + 1 < cols && free(end + 1, y) && this.isBlockingCell(end + 1, y - 1)) end++;
        if (end - x >= 2 && rng.chance(0.6)) for (let i = x; i <= end; i++) put(TILE_FRAMES.decorPipe, i, y);
        x = end + 1;
      }
    }
    // Warning stripes along some floors inside back-wall bays.
    for (let y = 0; y < rows - 1; y++) {
      let x = 0;
      while (x < cols) {
        if (!(free(x, y) && this.isBlockingCell(x, y + 1))) {
          x++;
          continue;
        }
        let end = x;
        while (end + 1 < cols && free(end + 1, y) && this.isBlockingCell(end + 1, y + 1)) end++;
        if (end - x >= 3 && rng.chance(0.25)) {
          const len = Math.min(end - x + 1, rng.int(2, 4));
          const s = rng.int(x, end - len + 1);
          for (let i = s; i < s + len; i++) put(TILE_FRAMES.decorStripes, i, y);
        }
        x = end + 1;
      }
    }
    // Scattered vents and grates, never touching each other.
    for (let y = 1; y < rows - 1; y++) {
      for (let x = 1; x < cols - 1; x++) {
        if (!free(x, y)) continue;
        let crowded = false;
        for (let dy = -1; dy <= 1 && !crowded; dy++) for (let dx = -1; dx <= 1; dx++) if (used[(y + dy) * cols + x + dx]) crowded = true;
        if (crowded) continue;
        const r = rng.next();
        if (r < 0.035) put(TILE_FRAMES.decorVent, x, y);
        else if (r < 0.06 && free(x, y + 1)) {
          put(TILE_FRAMES.decorGrate, x, y);
          if (rng.chance(0.5)) put(TILE_FRAMES.decorGrate, x, y + 1);
        }
      }
    }
  }

  // ───────────────────────────── Queries ─────────────────────────────

  kindAtTile(tx: number, ty: number): CellKind {
    if (tx < 0 || tx >= this.cols || ty >= this.rows) return CELL.SOLID;
    if (ty < 0) return CELL.EMPTY;
    return this.cells[ty * this.cols + tx] as CellKind;
  }

  kindAt(x: number, y: number): CellKind {
    return this.kindAtTile(Math.floor(x / TILE), Math.floor(y / TILE));
  }

  /** Solid walls/floors and hazards (one-way platforms are not solid for projectiles). */
  isSolidAt(x: number, y: number): boolean {
    const k = this.kindAt(x, y);
    return k === CELL.SOLID || k === CELL.HAZARD;
  }

  isOneWayAt(x: number, y: number): boolean {
    return this.kindAt(x, y) === CELL.ONEWAY;
  }

  isHazardAt(x: number, y: number): boolean {
    return this.kindAt(x, y) === CELL.HAZARD;
  }

  /** Grid DDA from (x0,y0) to (x1,y1) against solid/hazard cells. */
  raycast(x0: number, y0: number, x1: number, y1: number): { x: number; y: number; hit: boolean; dist: number } {
    const dx = x1 - x0;
    const dy = y1 - y0;
    const len = Math.hypot(dx, dy);
    if (this.isSolidAt(x0, y0)) return { x: x0, y: y0, hit: true, dist: 0 };
    if (len < 1e-6) return { x: x1, y: y1, hit: false, dist: 0 };
    const ux = dx / len;
    const uy = dy / len;
    let tx = Math.floor(x0 / TILE);
    let ty = Math.floor(y0 / TILE);
    const stepX = ux > 0 ? 1 : -1;
    const stepY = uy > 0 ? 1 : -1;
    const tDeltaX = ux !== 0 ? Math.abs(TILE / ux) : Infinity;
    const tDeltaY = uy !== 0 ? Math.abs(TILE / uy) : Infinity;
    let tMaxX = ux !== 0 ? ((ux > 0 ? (tx + 1) * TILE : tx * TILE) - x0) / ux : Infinity;
    let tMaxY = uy !== 0 ? ((uy > 0 ? (ty + 1) * TILE : ty * TILE) - y0) / uy : Infinity;
    // Bounded walk: a ray can cross at most cols + rows cells (plus a margin outside the map).
    for (let i = 0; i < this.cols + this.rows + 8; i++) {
      let t: number;
      if (tMaxX < tMaxY) {
        t = tMaxX;
        tMaxX += tDeltaX;
        tx += stepX;
      } else {
        t = tMaxY;
        tMaxY += tDeltaY;
        ty += stepY;
      }
      if (t > len) break;
      const k = this.kindAtTile(tx, ty);
      if (k === CELL.SOLID || k === CELL.HAZARD) return { x: x0 + ux * t, y: y0 + uy * t, hit: true, dist: t };
    }
    return { x: x1, y: y1, hit: false, dist: len };
  }

  /** Top y of the first standable surface at or below (x, y) within maxDist, or null. */
  groundBelow(x: number, y: number, maxDist = 2000): number | null {
    const tx = Math.floor(x / TILE);
    if (tx < 0 || tx >= this.cols) return null;
    let ty = Math.max(0, Math.floor(y / TILE));
    // Starting inside ground deeper than its top: skip to the next open cell first.
    if (this.kindAtTile(tx, ty) !== CELL.EMPTY && ty * TILE < y - 0.5) {
      while (ty < this.rows && this.kindAtTile(tx, ty) !== CELL.EMPTY) ty++;
    }
    for (; ty < this.rows; ty++) {
      const top = ty * TILE;
      if (top - y > maxDist) return null;
      if (this.kindAtTile(tx, ty) !== CELL.EMPTY) return top;
    }
    return null;
  }

  // ───────────────────────────── Physics ─────────────────────────────

  addCollider(
    obj: Phaser.GameObjects.GameObject,
    opts?: { canDrop?: () => boolean; onHit?: () => void },
  ): Phaser.Physics.Arcade.Collider {
    const process = (o: unknown, t: unknown): boolean => {
      const tile = t as Phaser.Tilemaps.Tile;
      if (!PLATFORM_FRAMES.has(tile.index)) return true;
      const body = ((o as Phaser.GameObjects.GameObject).body ?? null) as Phaser.Physics.Arcade.Body | null;
      if (!body) return true;
      if (opts?.canDrop?.()) return false;
      if (body.velocity.y < 0) return false;
      // Only land when the feet were above the platform top in the previous step.
      return body.prev.y + body.height <= tile.pixelY + ONE_WAY_TOLERANCE;
    };
    const onHit = opts?.onHit ? () => opts.onHit!() : undefined;
    const collider = this.scene.physics.add.collider(obj, this.solids, onHit, process);
    this.colliders.push(collider);
    return collider;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const c of this.colliders) {
      try {
        c.destroy();
      } catch {
        // Physics world already torn down with the scene.
      }
    }
    this.colliders = [];
    try {
      this.map.destroy();
    } catch {
      // Scene shutdown may already have destroyed the layers.
    }
  }
}
